import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { digitalEmployees } from '../data';
import { EmployeeCategory } from '../types';
import TeslaRobot from '../components/TeslaRobot';

const ChatListPage: React.FC = () => {
  // Define all possible categories
  const allCategories: EmployeeCategory[] = [
    'HR', 'PMO', 'Developer', 'Personal Assistant', 'RFP Developer'
  ];

  // State for active category filter (null means show all)
  const [activeCategory, setActiveCategory] = useState<EmployeeCategory | null>(null);

  // Group employees by category
  const employeesByCategory = allCategories.reduce((acc, category) => {
    acc[category] = digitalEmployees.filter(employee => employee.category === category);
    return acc;
  }, {} as Record<EmployeeCategory, typeof digitalEmployees>);

  // Handle category filter click
  const handleCategoryClick = (category: EmployeeCategory) => {
    if (activeCategory === category) {
      setActiveCategory(null); // Toggle off if already active
    } else {
      setActiveCategory(category);
    }
  };

  // Get categories to display (all or filtered)
  const categoriesToDisplay = activeCategory ? [activeCategory] : allCategories;

  return (
    <div className="chat-list-page">
      <div className="chat-list-header">
        <div className="chat-list-title-container">
          <h1>
            Neural <span className="text-gradient">Chat</span>
          </h1>
          <p className="chat-list-subtitle">
            Select an AI Employee to start a conversation
          </p>
        </div>
        <div className="chat-list-robot">
          <TeslaRobot width={120} height={120} />
        </div>
      </div>

      <div className="category-filter">
        <div className="filter-label">Filter by category:</div>
        <div className="category-buttons">
          {allCategories.map(category => (
            <button
              key={category}
              className={`category-button ${activeCategory === category ? 'active' : ''}`}
              onClick={() => handleCategoryClick(category)}
            >
              {category}
            </button>
          ))}
          {activeCategory && (
            <button
              className="category-button clear-filter"
              onClick={() => setActiveCategory(null)}
            >
              Clear Filter
            </button>
          )}
        </div>
      </div>

      {categoriesToDisplay.map(category => (
        <div key={category} className="category-section">
          <h2 className="category-title">{category}</h2>
          <div className="chat-list-grid">
            {employeesByCategory[category].map(employee => (
              <Link
                to={`/chat/${employee.id}`}
                key={employee.id}
                className="chat-employee-card"
              >
                <div className="chat-employee-avatar">
                  <img src={employee.avatar} alt={employee.name} />
                </div>
                <div className="chat-employee-info">
                  <h3>{employee.name}</h3>
                  <p className="chat-employee-role">{employee.category}</p>
                  <div className="chat-employee-skills">
                    {employee.skills.slice(0, 3).map((skill, index) => (
                      <span key={index} className="chat-skill-tag">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="chat-start-button">
                  <span className="chat-icon">💬</span> Start Chat
                </div>
              </Link>
            ))}
          </div>
        </div>
      ))}

      {activeCategory && employeesByCategory[activeCategory].length === 0 && (
        <div className="no-employees-message">
          No AI Employees found in the {activeCategory} category.
        </div>
      )}
    </div>
  );
};

export default ChatListPage;
